import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getCourseById, getRelatedCourses } from '@/mocks/courses';

const articleImages: Record<string, string> = {
  'jp-lang-001': 'https://readdy.ai/api/search-image?query=Japanese%20language%20learning%20minimalist%20design%2C%20warm%20lighting%2C%20clean%20background%2C%20professional%20atmosphere%2C%20scholarly%20vibe&width=1200&height=600&seq=article-jp-lang-001&orientation=landscape',
  'jp-career-001': 'https://readdy.ai/api/search-image?query=Japanese%20business%20office%20minimalist%20design%2C%20warm%20lighting%2C%20professional%20atmosphere%2C%20modern%20workspace&width=1200&height=600&seq=article-jp-career-001&orientation=landscape',
  'jp-culture-001': 'https://readdy.ai/api/search-image?query=Traditional%20Japanese%20tea%20ceremony%20minimalist%2C%20warm%20natural%20lighting%2C%20serene%20tatami%20room%2C%20elegant&width=1200&height=600&seq=article-jp-culture-001&orientation=landscape',
  'kr-lang-001': 'https://readdy.ai/api/search-image?query=Korean%20language%20learning%20minimalist%20design%2C%20warm%20lighting%2C%20clean%20background%2C%20professional%20scholarly&width=1200&height=600&seq=article-kr-lang-001&orientation=landscape',
  'kr-career-001': 'https://readdy.ai/api/search-image?query=Korean%20business%20office%20minimalist%20design%2C%20modern%20workspace%2C%20warm%20lighting%2C%20professional&width=1200&height=600&seq=article-kr-career-001&orientation=landscape',
  'kr-culture-001': 'https://readdy.ai/api/search-image?query=Korean%20pop%20culture%20and%20media%20minimalist%2C%20creative%20atmosphere%2C%20warm%20lighting%2C%20vibrant&width=1200&height=600&seq=article-kr-culture-001&orientation=landscape',
};

const relatedImages: Record<string, string> = {
  'jp-lang-001': 'https://readdy.ai/api/search-image?query=Japanese%20language%20study%20minimalist%2C%20warm%20lighting%2C%20clean%20background&width=400&height=280&seq=rel-jp-lang-001&orientation=landscape',
  'jp-career-001': 'https://readdy.ai/api/search-image?query=Japanese%20business%20office%20minimalist%2C%20warm%20lighting%2C%20professional&width=400&height=280&seq=rel-jp-career-001&orientation=landscape',
  'jp-culture-001': 'https://readdy.ai/api/search-image?query=Japanese%20tea%20ceremony%20minimalist%2C%20warm%20lighting%2C%20serene%20tatami&width=400&height=280&seq=rel-jp-culture-001&orientation=landscape',
  'kr-lang-001': 'https://readdy.ai/api/search-image?query=Korean%20language%20study%20minimalist%2C%20warm%20lighting%2C%20clean%20background&width=400&height=280&seq=rel-kr-lang-001&orientation=landscape',
  'kr-career-001': 'https://readdy.ai/api/search-image?query=Korean%20business%20office%20minimalist%2C%20modern%20workspace&width=400&height=280&seq=rel-kr-career-001&orientation=landscape',
  'kr-culture-001': 'https://readdy.ai/api/search-image?query=Korean%20pop%20culture%20minimalist%2C%20creative%20atmosphere&width=400&height=280&seq=rel-kr-culture-001&orientation=landscape',
};

function renderContent(content: string) {
  const sections = content.split('\n## ').filter(Boolean);
  return sections.map((section, idx) => {
    const lines = section.split('\n');
    const heading = lines[0].replace(/^## /, '');
    const bodyLines = heading === lines[0] ? lines.slice(1) : lines;

    if (idx === 0 && !section.startsWith('##')) {
      return (
        <div key={idx}>
          <p className="text-foreground-600 leading-relaxed mb-4">{section.trim()}</p>
        </div>
      );
    }

    const subSections: { subHeading: string; items: string[] }[] = [];
    let currentSub: { subHeading: string; items: string[] } | null = null;

    for (const line of bodyLines) {
      if (line.startsWith('### ')) {
        if (currentSub) subSections.push(currentSub);
        currentSub = { subHeading: line.replace('### ', '').trim(), items: [] };
      } else if (line.startsWith('- ')) {
        if (currentSub) {
          currentSub.items.push(line.replace('- ', '').trim());
        }
      } else if (line.trim()) {
        if (!currentSub) {
          currentSub = { subHeading: '', items: [] };
        }
        currentSub.items.push(line.trim());
      }
    }
    if (currentSub) subSections.push(currentSub);

    return (
      <div key={idx} className="mt-8">
        {heading && <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">{heading}</h2>}
        {subSections.map((sub, si) => (
          <div key={si} className={sub.subHeading ? 'mb-6' : 'mb-4'}>
            {sub.subHeading && (
              <h3 className="text-lg font-heading font-semibold text-foreground-800 mb-3">{sub.subHeading}</h3>
            )}
            <ul className="space-y-2">
              {sub.items.map((item, ii) => (
                <li key={ii} className="flex items-start gap-2 text-foreground-600 text-sm leading-relaxed">
                  <span className="text-primary-500 mt-0.5 flex-shrink-0">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  });
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const course = id ? getCourseById(id) : undefined;

  if (!course) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-24">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-3">课程未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查找的课程不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedCourses = getRelatedCourses(course);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: course.zoneLabel, href: `/category/zone-${course.zone === '一区' ? 'jp' : 'kr'}` },
    { label: course.category, href: `/category/${course.categorySlug}` },
    { label: course.title },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <article>
          <div className="bg-background-100 pt-24 pb-8">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <div className="relative w-full h-[350px] md:h-[450px] overflow-hidden">
            <img
              src={articleImages[course.id] || 'https://readdy.ai/api/search-image?query=education%20minimalist%20design%2C%20warm%20lighting%2C%20clean%20background%2C%20professional%20atmosphere&width=1200&height=600&seq=article-fallback&orientation=landscape'}
              alt={course.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="w-full max-w-4xl mx-auto">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    course.zone === '一区' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
                  }`}>
                    {course.zoneLabel}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/90 text-foreground-700">
                    {course.category}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/90 text-foreground-700">
                    {course.level}
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-background-50 mb-3">
                  {course.title}
                </h1>
                <p className="text-background-50/80 text-sm md:text-base max-w-2xl">
                  {course.shortDescription}
                </p>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-10">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-background-200 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{course.author}</p>
                      <p className="text-xs text-foreground-500">主讲老师</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-time-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{course.readTime}</p>
                      <p className="text-xs text-foreground-500">课程时长</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{course.rating} 分</p>
                      <p className="text-xs text-foreground-500">{course.students.toLocaleString()} 学员</p>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-3">课程概述</h2>
                  <p className="text-foreground-600 leading-relaxed">{course.description}</p>
                </div>

                {course.content && (
                  <div>{renderContent(course.content)}</div>
                )}

                <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">课程标签：</span>
                  {course.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="lg:w-80 flex-shrink-0">
                <div className="sticky top-24 bg-background-100 rounded-xl p-6">
                  <p className="text-3xl font-bold text-primary-600 mb-1">{course.price}</p>
                  <p className="text-xs text-foreground-500 mb-6">一次购买，永久学习</p>

                  <button className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-colors mb-3 whitespace-nowrap">
                    立即报名
                  </button>
                  <button className="w-full py-3 rounded-lg bg-background-200 text-foreground-700 font-medium text-sm hover:bg-background-300 transition-colors mb-6 whitespace-nowrap">
                    <i className="ri-heart-line mr-1.5"></i>收藏课程
                  </button>

                  <div className="space-y-3 pt-4 border-t border-background-200">
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-checkbox-circle-line text-green-500"></i>
                      <span className="text-foreground-700">终身学习权限</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-checkbox-circle-line text-green-500"></i>
                      <span className="text-foreground-700">课后答疑辅导</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-checkbox-circle-line text-green-500"></i>
                      <span className="text-foreground-700">学习资料下载</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-checkbox-circle-line text-green-500"></i>
                      <span className="text-foreground-700">完成证书颁发</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-background-200">
                    <div className="flex items-center gap-2 text-sm text-foreground-500">
                      <i className="ri-share-forward-line"></i>
                      <span>分享课程：</span>
                      <a href="#" className="text-primary-600 hover:text-primary-700" aria-label="分享到微信">
                        <i className="ri-wechat-line"></i>
                      </a>
                      <a href="#" className="text-primary-600 hover:text-primary-700" aria-label="分享到微博">
                        <i className="ri-weibo-line"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {relatedCourses.length > 0 && (
            <div className="bg-background-100 py-16">
              <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                  相关推荐课程
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
                  {relatedCourses.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={relatedImages[related.id] || 'https://readdy.ai/api/search-image?query=education%20minimalist%2C%20warm%20lighting%2C%20clean%20background&width=400&height=280&seq=rel-fallback&orientation=landscape'}
                          alt={related.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute top-2 left-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            related.zone === '一区' ? 'bg-red-500/90 text-white' : 'bg-amber-500/90 text-white'
                          }`}>
                            {related.zoneLabel}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-primary-600 font-medium">{related.category}</span>
                        <h3 className="text-sm font-semibold text-foreground-900 mt-1 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {related.title}
                        </h3>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <i className="ri-star-fill text-amber-400 text-xs"></i>
                            <span className="text-xs font-medium text-foreground-700">{related.rating}</span>
                          </div>
                          <span className="text-sm font-bold text-primary-600">{related.price}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}